import React, { useEffect, useRef, useState, useCallback } from "react"
import ReactSelect, { components } from "react-select"
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'

export const MultiSelectDropdown = props => {
  const { getOptionValue = o => o.value, disabled = false } = props
  const { styles, ...rest } = props
  // captured for the inner Option component, whose parameter `props` shadows
  // the outer props
  const outerProps = props
  const [currentInput, setCurrentInput] = useState('')
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isIpad = getCurrentScreenType() === 'ipad'

  // isOptionSelected sees previous props.value after onChange
  const valueRef = useRef(props.value);
  valueRef.current = props.value || [];

  const selectAllOption =
    props.includeSelectAll
      ? props.selectAllOption ||
      {
        value: "<SELECT_ALL>",
        label: "Select all"
      }
      : null

  const closeMenu = useCallback((event) => {
    const target = event.target;
    if (!target.closest('.react-select__menu-list')) {
      setMenuIsOpen(false)
      props?.onMenuClose?.()
    }
  }, [props?.onMenuClose])

  useEffect(() => {
    if (!isIpad) return;
    document.addEventListener('click', closeMenu, true);
    return () => {
      document.removeEventListener('click', closeMenu, true);
    };
  }, [isIpad, closeMenu]);

  const isSelectAllSelected = () =>
    valueRef.current.length === props.options.length;

  const isOptionSelected = option =>
    valueRef.current.some((item) => getOptionValue(item) === getOptionValue(option)) ||
    isSelectAllSelected();

  const getOptions = () => props.includeSelectAll ? [selectAllOption, ...props.options] : props.options

  const getValue = () =>
    props.includeSelectAll && isSelectAllSelected() ? [selectAllOption] : props.value;

  const handleInputChange = (newValue, actionMeta) => {
    setCurrentInput(newValue)
  }

  const handleChange = useCallback((newValue, actionMeta) => {
    const { action, option, removedValue } = actionMeta;
    const selectAllOptionValue = props.includeSelectAll ? getOptionValue(selectAllOption) : null
    const removedValueValue = (action === "remove-value") ? getOptionValue(removedValue) : null

    if (props.includeSelectAll && action === "select-option" && getOptionValue(option) === selectAllOptionValue) {
      props.onChange(props.options, actionMeta);
    } else if (
      (action === "deselect-option" &&
        getOptionValue(option) === selectAllOptionValue) ||
      (action === "remove-value" &&
        removedValueValue === selectAllOptionValue)
    ) {
      props.onChange([], actionMeta);
    } else if (
      actionMeta.action === "deselect-option" &&
      valueRef.current.length === props.options.length
    ) {
      props.onChange(
        props.options.filter(opt => {
          return getOptionValue(opt) !== getOptionValue(option)
        }),
        actionMeta
      );
    } else {
      props.onChange(newValue || [], actionMeta);
    }
  }, [
    props.includeSelectAll,
    props.onChange,
    props.options,
    getOptionValue
  ])

  const onChange = useCallback((newValue, actionMeta) => {
    if (isIpad && ['select-option', 'deselect-option'].includes(actionMeta.action)) {
      setTimeout(() => {
        setMenuIsOpen(true)
        handleChange(newValue, actionMeta)
      }, 0);
    } else {
      handleChange(newValue, actionMeta)
    }
  }, [isIpad])

  const Option = (props) => {
    return (
      <div
        className={'option'}
        style={{ textAlign: 'left' }}
        data-testid={`select-option-${props.label}`}
      >
        <components.Option {...props}>
          <input
            id={props.label + ' choice'}
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />
          {" "}
          <label htmlFor={props.label + ' choice'}>
            {props.label}
          </label>
          {outerProps.getOptionAdornment
            ? outerProps.getOptionAdornment(props.data)
            : null}
        </components.Option>
      </div>
    )
  }

  const MultiValue = ({ index, getValue, ...componentProps }) => {
    const { allSelectedPlaceholder, getOptionLabel, multipleSelectedPlaceholder } = props
    const multipleSelected = valueRef.current.length > 1
    const allSelected = isSelectAllSelected()
    const userIsTyping = currentInput !== ''

    // fix #8981
    if (valueRef.current[index] === undefined) {
      return null
    }

    const label = getOptionLabel
      ? getOptionLabel(valueRef.current[index])
      : valueRef.current[index]['label']
    return userIsTyping
      ? ''
      : index === 0
        ? allSelected
          ? (allSelectedPlaceholder || 'All selected')
          : multipleSelected
            ? (multipleSelectedPlaceholder || 'Multiple')
            : label
        : null
  }

  const theme = (theme) => ({
    ...theme,
    spacing: {
      ...theme.spacing,
      controlHeight: '100%',
      baseUnit: 2,
    }
  })

  return (
    <div
      className={'multi-select-dropdown'}
      data-toggle={props['data-toggle']}
      data-title={props['data-title']}
    >
      <ReactSelect
        classNamePrefix="react-select"
        theme={theme}
        isSearchable={isIpad ? false : true}
        components={{ MultiValue, Option }}
        closeMenuOnSelect={false}
        isClearable={false}
        hideSelectedOptions={false}
        isDisabled={disabled}
        styles={{
          ...styles,
          menuPortal: (provided) => ({ ...provided, zIndex: props.menuPortalStyles?.zIndex || 9999, width: props.menuPortalStyles?.width || provided.width }),
          menu: (provided) => ({ ...provided, position: 'absolute', zIndex: 9999 }),
          option: (styles) => ({ ...styles, textOverflow: 'ellipsis', display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center' }),
        }}
        menuPortalTarget={document.body}
        {...rest}
        isOptionSelected={isOptionSelected}
        options={getOptions()}
        value={getValue()}
        onInputChange={handleInputChange}
        isMulti
        {...(isIpad && {
          menuIsOpen,
          onMenuOpen: () => setMenuIsOpen(true),
          onMenuClose: closeMenu
        })}
        onChange={onChange}
      />
    </div>
  )
}

export default MultiSelectDropdown
